import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Input, Select, Form, Row, Col, Alert } from 'antd';

import roles from "../data/roles";
import { postUser } from "../api/user";
import AuthResponse from "../types/AuthResponse";
import CreateUserRequest from "../types/CreateUserRequest";
import { fetchCities } from "../api/city";
import EntityInfo from "../types/EntityInfo";
import { useTexts } from "../hooks/useTexts";
import { selectFilterOption } from "../util/common";

type UserData = {
    username: string,
    password: string,
    firstname: string,
    lastname: string,
    roleId: number | undefined,
    cityIds: number[]
};

export default function UserRegistration(): JSX.Element {
    const { t } = useTexts();
    const [cities, setCities] = useState<EntityInfo[]>([]);
    const [loading, isLoading] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState<UserData>({
        username: '', password: '',
        firstname: '', lastname: '', roleId: undefined,
        cityIds: []
    });
    const navigate = useNavigate();

    const getCities = () => {
        fetchCities().then(response => {
            setCities(response?.items || [])
        })
            .catch(error => console.error("ERROR", error))
    }

    React.useEffect(() => {
        if (loading) {
            handleSubmit();
            isLoading(false);
        }
    }, [loading]);

    const fetchData = async () => {
        const request: CreateUserRequest = {
            username: data.username || '',
            password: data.password || '',
            firstname: data.firstname || '',
            lastname: data.lastname || '',
            roleId: !!data.roleId ? Number(data.roleId) : 0,
            cityIds: data.cityIds
        }
        setError('');
        const response: AuthResponse | any = await postUser(request);
        if (!!response?.error) {
            setError(response?.error);
        } else {
            toast.success(t('user', 'userCreated'), { theme: "colored" })
            navigate("/")
        }
    }

    useEffect(() => {
        getCities();
    }, [])

    const handleSubmit = (): void => {
        fetchData();
    };

    return (
        <div style={{ marginTop: '48px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
            {!!error && (
                <div style={{ marginBottom: '16px' }}>
                    <Alert message={error} type="error" showIcon style={{ marginBottom: '8px' }} />
                </div>
            )}

            <div style={{ background: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <Form layout="vertical" className="responsive-form">
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item label={t('auth', 'username')} required>
                                <Input
                                    value={data.username}
                                    onChange={(e) => setData({ ...data, username: e.target.value })}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label={t('auth', 'password')} required>
                                <Input.Password
                                    value={data.password}
                                    onChange={(e) => setData({ ...data, password: e.target.value })}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label={t('user', 'firstname')} required>
                                <Input
                                    value={data.firstname}
                                    onChange={(e) => setData({ ...data, firstname: e.target.value })}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label={t('user', 'lastname')} required>
                                <Input
                                    value={data.lastname}
                                    onChange={(e) => setData({ ...data, lastname: e.target.value })}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label={t('user', 'role')} required>
                                <Select
                                    placeholder={t('common', 'none')}
                                    value={data.roleId}
                                    onChange={(value) => setData({ ...data, roleId: value })}
                                    options={roles.map((role) => ({ label: role.name_az, value: role.id }))}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label={t('user', 'selectCity')}>
                                <Select
                                    mode="multiple"
                                    placeholder={t('common', 'none')}
                                    value={data.cityIds}
                                    onChange={(value) => setData({ ...data, cityIds: value })}
                                    options={cities.map((city) => ({ label: city.name, value: city.id }))}
                                    allowClear
                                    showSearch
                                    filterOption={selectFilterOption}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Button
                                type="primary"
                                onClick={() => isLoading(true)}
                                loading={loading}
                                disabled={!data.username || !data.password || !data.firstname || !data.lastname}
                                block
                            >
                                {t('user', 'createUser')}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </div>
        </div>
    );
}
